import { IExcelReportData, ITableData, IRowCells } from 'types/excelExport';
import { IOperatingPeriodCashFlows } from 'views/ProFormaTable/types';
import {
    createDataRow,
    createBlankRow,
    parseNumber,
    getMonthLabel,
} from './helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from './constants';
import { getModelExportConfig } from './modelConfig';

interface ICashFlowSheetParams {
    dealName: string;
    cashFlows: (IOperatingPeriodCashFlows | null)[];
    startDate: string;
    proFormaModelId: number;
    showCents?: boolean;
    includeGainLossToLease?: boolean;
}

const createMonthlyHeaderRow = (startDate: string, monthCount: number): ITableData => {
    const cells: IRowCells[] = [{ value: '', width: 30 }];
    for (let i = 0; i < monthCount; i++) {
        cells.push({ value: getMonthLabel(startDate, i), width: 12, alignment: { horizontal: 'right' } });
    }
    return {
        rowCells: cells,
        rowFill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_COLORS.BLUE_HEADER },
        },
        rowFont: { bold: true, color: { argb: EXCEL_COLORS.WHITE } },
    };
};

const createSectionHeader = (label: string, colCount: number): ITableData => ({
    rowCells: [
        { value: label, cellFont: { bold: true }, width: 30 },
        ...Array(colCount - 1).fill({ value: '' }),
    ],
    rowFill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: EXCEL_COLORS.DARK_GRAY },
    },
});

const createCashFlowRow = (
    label: string,
    cashFlows: IOperatingPeriodCashFlows[],
    getValue: (cf: IOperatingPeriodCashFlows) => string | number | null | undefined,
    showCents: boolean = false,
    bold: boolean = false,
    isPercent: boolean = false,
    bgColor?: string,
    borderColor?: string,
): ITableData => {
    const border = borderColor ? {
        top: { style: 'thin', color: { argb: borderColor } },
        bottom: { style: 'thin', color: { argb: borderColor } },
    } : undefined;

    const cells: IRowCells[] = [
        {
            value: label,
            width: 30,
            cellFont: bold ? { bold: true } : undefined,
            border,
        },
    ];

    const getNumFmt = () => {
        if (isPercent) return EXCEL_NUM_FORMATS.PERCENT;
        return showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;
    };

    cashFlows.forEach(cf => {
        const value = getValue(cf);
        const parsedValue = parseNumber(value) ?? 0;
        cells.push({
            value: parsedValue,
            width: 12,
            alignment: { horizontal: 'right' },
            cellFont: bold ? { bold: true } : undefined,
            border,
            numFmt: getNumFmt(),
        });
    });

    const row = createDataRow(cells, bold);
    if (bgColor) {
        row.rowFill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: bgColor },
        };
    }
    return row;
};

export const formatCashFlowSheet = ({
    dealName,
    cashFlows,
    startDate,
    proFormaModelId,
    showCents = false,
    includeGainLossToLease = true,
}: ICashFlowSheetParams): IExcelReportData => {
    const config = getModelExportConfig(proFormaModelId);

    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const validCashFlows = cashFlows.filter(cf => cf !== null) as IOperatingPeriodCashFlows[];
    const displayCashFlows = validCashFlows;
    const colCount = displayCashFlows.length + 1;

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: 'Pro Forma Cash Flow Schedule', cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(colCount),
        createMonthlyHeaderRow(startDate, displayCashFlows.length),
    ];

    const { cashFlowTab } = config;

    const incomeRows: ITableData[] = [
        createSectionHeader(cashFlowTab.income.sectionHeader, colCount),
        createCashFlowRow(cashFlowTab.income.grossRentLabel, displayCashFlows, cf => cf.grossRent, showCents),
    ];

    if (cashFlowTab.income.concessions) {
        incomeRows.push(createCashFlowRow(cashFlowTab.income.concessions, displayCashFlows, cf => cf.concessions, showCents));
    }

    if (cashFlowTab.income.gainLossToLease && includeGainLossToLease) {
        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.gainLossToLease, displayCashFlows, cf => parseNumber(cf.gainLossToLease), showCents,
        ));
    }

    if (cashFlowTab.income.nonRevenueUnits) {
        incomeRows.push(createCashFlowRow(cashFlowTab.income.nonRevenueUnits, displayCashFlows, cf => cf.nonRevenueUnits, showCents));
    }

    incomeRows.push(createCashFlowRow(
        cashFlowTab.income.totalRentalIncome,
        displayCashFlows,
        cf => cf.totalRentalIncome,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));
    incomeRows.push(createBlankRow(colCount));

    // Add other income line items dynamically
    // Build index map for O(1) lookups
    const otherIncomeIndexMap = new Map<string, number>();
    displayCashFlows[0]?.otherIncome?.forEach((item, idx) => {
        otherIncomeIndexMap.set(item.name, idx);
    });

    if (displayCashFlows.length > 0 && displayCashFlows[0].otherIncome) {
        displayCashFlows[0].otherIncome.forEach(income => {
            const idx = otherIncomeIndexMap.get(income.name);
            incomeRows.push(createCashFlowRow(
                income.name,
                displayCashFlows,
                cf => (idx !== undefined ? cf.otherIncome?.[idx]?.value : undefined),
                showCents,
            ));
        });
    }

    incomeRows.push(createCashFlowRow(
        cashFlowTab.income.totalOtherIncome,
        displayCashFlows,
        cf => cf.totalOtherIncome,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));
    incomeRows.push(createBlankRow(colCount));
    incomeRows.push(createCashFlowRow(
        cashFlowTab.income.totalPotentialIncome,
        displayCashFlows,
        cf => cf.totalPotentialIncome,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));

    const adjustmentsRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.adjustments.sectionHeader, colCount),
    ];

    // Add income adjustment line items dynamically
    const incomeAdjustmentsIndexMap = new Map<string, number>();
    displayCashFlows[0]?.incomeAdjustments?.forEach((item, idx) => {
        incomeAdjustmentsIndexMap.set(item.name, idx);
    });

    if (displayCashFlows.length > 0 && displayCashFlows[0].incomeAdjustments) {
        displayCashFlows[0].incomeAdjustments.forEach(adjustment => {
            const idx = incomeAdjustmentsIndexMap.get(adjustment.name);
            adjustmentsRows.push(createCashFlowRow(
                adjustment.name,
                displayCashFlows,
                cf => (idx !== undefined ? cf.incomeAdjustments?.[idx]?.value : undefined),
                showCents,
            ));
        });
    }

    adjustmentsRows.push(createCashFlowRow(
        cashFlowTab.adjustments.totalIncomeAdjustments,
        displayCashFlows,
        cf => cf.totalIncomeAdjustments,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));
    adjustmentsRows.push(createBlankRow(colCount));
    adjustmentsRows.push(createCashFlowRow(
        cashFlowTab.effectiveGrossRevenue.sectionHeader,
        displayCashFlows,
        cf => cf.effectiveGrossRevenue,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
        EXCEL_COLORS.DARK_GRAY,
    ));

    const expenseRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.expenses.sectionHeader, colCount),
    ];

    const operatingExpensesIndexMap = new Map<string, number>();
    displayCashFlows[0]?.operatingExpenses?.forEach((item, idx) => {
        operatingExpensesIndexMap.set(item.name, idx);
    });

    if (displayCashFlows.length > 0 && displayCashFlows[0].operatingExpenses) {
        displayCashFlows[0].operatingExpenses.forEach(expense => {
            const idx = operatingExpensesIndexMap.get(expense.name);
            expenseRows.push(createCashFlowRow(
                expense.name,
                displayCashFlows,
                cf => (idx !== undefined ? cf.operatingExpenses?.[idx]?.value : undefined),
                showCents,
            ));
        });
    }

    expenseRows.push(createCashFlowRow(
        cashFlowTab.expenses.totalOperatingExpenses,
        displayCashFlows,
        cf => cf.totalOperatingExpenses,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));
    expenseRows.push(createBlankRow(colCount));
    expenseRows.push(createCashFlowRow(
        cashFlowTab.netOperatingIncome.sectionHeader,
        displayCashFlows,
        cf => cf.netOperatingIncome,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
        EXCEL_COLORS.DARK_GRAY,
    ));

    const capexRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.capex.sectionHeader, colCount),
    ];

    const capitalExpendituresIndexMap = new Map<string, number>();
    displayCashFlows[0]?.capitalExpenditures?.forEach((item, idx) => {
        capitalExpendituresIndexMap.set(item.name, idx);
    });

    if (displayCashFlows.length > 0 && displayCashFlows[0].capitalExpenditures) {
        displayCashFlows[0].capitalExpenditures.forEach(capex => {
            const idx = capitalExpendituresIndexMap.get(capex.name);
            capexRows.push(createCashFlowRow(
                capex.name,
                displayCashFlows,
                cf => (idx !== undefined ? cf.capitalExpenditures?.[idx]?.value : undefined),
                showCents,
            ));
        });
    }

    capexRows.push(createCashFlowRow(
        cashFlowTab.capex.totalCapitalExpenditures,
        displayCashFlows,
        cf => cf.totalCapitalExpenditures,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));
    capexRows.push(createBlankRow(colCount));
    capexRows.push(createCashFlowRow(
        cashFlowTab.cashFlowFromOperations.sectionHeader,
        displayCashFlows,
        cf => cf.cashFlowFromOperations,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
        EXCEL_COLORS.DARK_GRAY,
    ));

    const occupancyRows: ITableData[] = [];

    occupancyRows.push(createBlankRow(colCount));
    occupancyRows.push(createBlankRow(colCount));
    occupancyRows.push(createSectionHeader(cashFlowTab.occupancyMetrics.sectionHeader, colCount));

    if (cashFlowTab.occupancyMetrics.leasedPercent) {
        occupancyRows.push(createCashFlowRow(
            cashFlowTab.occupancyMetrics.leasedPercent,
            displayCashFlows,
            cf => parseNumber(cf.leasedPercentage),
            showCents,
            false,
            true,
        ));
    }

    if (cashFlowTab.occupancyMetrics.economicOccupancy) {
        occupancyRows.push(createCashFlowRow(
            cashFlowTab.occupancyMetrics.economicOccupancy,
            displayCashFlows,
            cf => parseNumber(cf.economicOccupancyPercentage),
            showCents,
            false,
            true,
        ));
    }

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [],
                tableData: [
                    ...incomeRows,
                    ...adjustmentsRows,
                    ...expenseRows,
                    ...capexRows,
                    ...occupancyRows,
                ],
            },
        ],
    };
};
